import { useState } from "react";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import withRouter from "hoc/withRouter";
import { useLoadAll } from "hooks/useLoadAll";
import {
  MdAdd,
  MdArrowBack,
  MdArrowForward,
  MdCheckCircle,
  MdDescription,
  MdHistory,
  MdInventory2,
  MdLocalShipping,
  MdLuggage,
  MdPayments,
  MdPerson,
  MdPersonOutline,
  MdPlace,
  MdReceiptLong,
  MdRoute,
  MdTrain,
} from "react-icons/md";
import { Link } from "react-router-dom";
import { snack } from "components/Snack/Snack";
import baggageTripsService from "services/admin/baggageTripsService";
import {
  baggageStatusColor,
  baggageStatusLabel,
} from "pages/Admin/Baggages/Baggage/baggage-status";
import { openWaybill } from "./waybill";
import "./baggage-detail.scss";

const PAYMENT_LABELS: Record<string, string> = {
  unpaid: "Не оплачено",
  paid: "Оплачено",
  refunded: "Возврат",
};

const PAYMENT_PILL: Record<string, string> = {
  paid: "bgd-pill--green",
  refunded: "bgd-pill--amber",
  unpaid: "bgd-pill--gray",
};

// Семантический цвет MUI (из baggageStatusColor) → класс пилюли в стиле v2.
const PILL_VARIANT: Record<string, string> = {
  default: "bgd-pill--gray",
  info: "bgd-pill--blue",
  primary: "bgd-pill--purple",
  success: "bgd-pill--green",
  warning: "bgd-pill--amber",
  error: "bgd-pill--red",
};

const statusPill = (status: string) => PILL_VARIANT[baggageStatusColor(status)] || "bgd-pill--gray";

// Линейный жизненный цикл места багажа. Отправка в целом не может быть «дальше»
// своего наименее продвинутого места, поэтому общий статус считаем по минимуму.
const STAGE_ORDER = ["registered", "accepted", "in_transit", "arrived", "delivered"];
const EXCEPTIONAL = ["lost", "damaged", "held"];

/** Свернуть статусы всех мест в один статус отправки + прогресс выдачи. */
const deriveShipmentStatus = (pieces: any[]) => {
  if (!pieces.length) return null;
  const stages = pieces.map((p) => p.status).filter((s) => STAGE_ORDER.includes(s));
  const overall = stages.length
    ? stages.reduce(
        (min, s) => (STAGE_ORDER.indexOf(s) < STAGE_ORDER.indexOf(min) ? s : min),
        stages[0],
      )
    : pieces[0].status;
  const delivered = pieces.filter((p) => p.status === "delivered").length;
  const exceptions = pieces.filter((p) => EXCEPTIONAL.includes(p.status)).length;
  const counts: Record<string, number> = {};
  pieces.forEach((p) => {
    counts[p.status] = (counts[p.status] || 0) + 1;
  });
  return { overall, delivered, exceptions, total: pieces.length, counts };
};

const formatDateTime = (d?: string) => (d ? new Date(d).toLocaleString("ru-RU") : "");

// Правильное склонение слова «место» (1 место, 2-4 места, 5+ мест).
const piecesWord = (n: number) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "место";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "места";
  return "мест";
};

/**
 * Единая история отправки = журналы перемещений всех её мест, свёрнутые в ленту.
 * События, случившиеся «одним действием» (одинаковый статус/станция/поезд/заметка
 * в пределах минуты — например регистрация всех мест), объединяем в одну запись,
 * собирая бирки задетых мест. Лента возвращается от новых к старым.
 */
const buildShipmentHistory = (pieces: any[]) => {
  const groups = new Map<string, any>();
  pieces.forEach((p) => {
    (p.history || []).forEach((h: any) => {
      const minute = h.createdAt ? Math.floor(new Date(h.createdAt).getTime() / 60000) : 0;
      const key = [h.status, h.stationID ?? "", h.trainID ?? "", (h.note || "").trim(), minute].join(
        "|",
      );
      let g = groups.get(key);
      if (!g) {
        g = {
          key,
          status: h.status,
          note: (h.note || "").trim(),
          station: h.station,
          train: h.train,
          scanner: h.scanner,
          createdAt: h.createdAt,
          tags: [] as string[],
        };
        groups.set(key, g);
      }
      // Момент группы — самое раннее событие в ней.
      if (h.createdAt && (!g.createdAt || new Date(h.createdAt) < new Date(g.createdAt))) {
        g.createdAt = h.createdAt;
      }
      if (p.tagCode && !g.tags.includes(p.tagCode)) g.tags.push(p.tagCode);
    });
  });
  return Array.from(groups.values()).sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
  );
};

// Стабильный цвет аватара по строке — как в дизайне v2 (Avatar).
const colorFromString = (input: string): string => {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 55%, 55%)`;
};

const initials = (name: string) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

interface BaggageTripDetailProps {
  router: any;
  data: any;
  loadData: () => void;
}

/** Пилюля-бейдж со статусной точкой — корпоративный стиль (как в Ticket v2). */
const Pill = ({
  variant,
  label,
  lg,
}: {
  variant: string;
  label: React.ReactNode;
  lg?: boolean;
}) => (
  <span className={`bgd-pill ${variant}${lg ? " lg" : ""}`}>
    <span className="bgd-pill-dot" />
    {label}
  </span>
);

/** Заголовок секции карточки: иконка-акцент + крупный «капс»-лейбл. */
const SectionHeader = ({
  icon,
  title,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}) => (
  <div className="bgd-section-header">
    <span className="bgd-section-title">
      {icon}
      {title}
    </span>
    {action}
  </div>
);

/** Поле «подпись над значением». */
const Field = ({
  label,
  value,
  wide,
  mono,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  wide?: boolean;
  mono?: boolean;
  muted?: boolean;
}) => (
  <div className={`bgd-field${wide ? " bgd-field--wide" : ""}`}>
    <span className="bgd-field-label">{label}</span>
    <span className={`bgd-field-value${mono ? " mono" : ""}${muted ? " muted" : ""}`}>{value}</span>
  </div>
);

const BaggageTripDetail = ({ router, data, loadData }: BaggageTripDetailProps) => {
  const {
    params: { id },
  } = router;
  const trip = data.trip;

  const [method, setMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [delivering, setDelivering] = useState(false);

  const customer = trip?.customer;
  const customerName = customer
    ? `${customer.lastName || ""} ${customer.firstName || ""}`.trim() || customer.name || "—"
    : "—";
  const customerIin = customer?.IDCard?.iin;
  const isLegal = trip?.clientType === "legal";

  const pieces: any[] = trip?.baggages || [];
  const currency = trip?.currency || "KZT";
  const paid = trip?.paymentStatus === "paid";

  // Параметры груза агрегируем из мест: общий вес, количество мест и описание.
  const totalWeight = pieces.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);
  const descriptions = Array.from(
    new Set(pieces.map((p) => (p.description || "").trim()).filter(Boolean)),
  );
  const cargoDescription = descriptions.length ? descriptions.join("; ") : "—";
  const hasDeclaredValue = trip?.declaredValue && Number(trip?.declaredValueAmount) > 0;
  const declaredValueText = hasDeclaredValue
    ? `${Number(trip.declaredValueAmount).toLocaleString("ru-RU")} ₸`
    : "Не указана";

  // Статус самой отправки — свёртка статусов всех мест багажа.
  const shipment = deriveShipmentStatus(pieces);

  // Выдать можно, когда отправка прибыла (есть места в статусе «прибыл»).
  const canDeliver = shipment?.overall === "arrived";

  // Общая история отправки — объединённые журналы перемещений всех мест.
  const history = buildShipmentHistory(pieces);

  const recipientName = (trip?.recipientName || "").trim();
  const hasSeparateRecipient = !!recipientName;

  const paymentStatus = trip?.paymentStatus;

  const handlePay = async () => {
    setSubmitting(true);
    try {
      await baggageTripsService.pay(id, { method });
      snack("Оплата принята");
      loadData();
    } catch (ex) {
      console.error(ex);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeliver = async () => {
    setDelivering(true);
    try {
      await baggageTripsService.deliver(id);
      snack("Отправка выдана получателю");
      loadData();
    } catch (ex) {
      console.error(ex);
    } finally {
      setDelivering(false);
    }
  };

  const handleReceipt = async () => {
    const res: any = await baggageTripsService.getReceiptPdf(id);
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
  };

  const handleLabels = async () => {
    const res: any = await baggageTripsService.getLabelsPdf(id);
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
  };

  // Накладная — печатная HTML-форма (см. waybill.ts): открывается в отдельном
  // окне и печатается/сохраняется в PDF. Тариф берётся через quote.
  const handleWaybill = () => {
    openWaybill(trip, baggageTripsService.quote).catch((ex) => {
      console.error(ex);
      snack("Не удалось сформировать накладную");
    });
  };

  return (
    <div className="bgd-root">
      {/* Шапка: номер отправки + статус отправки + статус оплаты */}
      <div className="bgd-header">
        <button className="bgd-back" onClick={() => router.navigate(-1)} aria-label="Назад">
          <MdArrowBack size={18} />
        </button>
        <div className="bgd-title">Отправка #{trip?.id}</div>
        <span className="bgd-header-spacer" />
        <div className="bgd-header-pills">
          {shipment && (
            <Pill
              variant={statusPill(shipment.overall)}
              label={baggageStatusLabel(shipment.overall)}
            />
          )}
          <Pill
            variant={PAYMENT_PILL[paymentStatus] || "bgd-pill--gray"}
            label={PAYMENT_LABELS[paymentStatus] || paymentStatus}
          />
        </div>
      </div>

      <div className="bgd-layout">
        {/* Основная колонка */}
        <div className="bgd-col">
          {/* Маршрут перевозки */}
          <div className="bgd-card">
            <SectionHeader icon={<MdRoute />} title="Маршрут перевозки" />
            <div className="bgd-route">
              <div className="bgd-route-col">
                <div className="bgd-route-label">Откуда</div>
                <div className="bgd-route-station">{trip?.originStation?.name || "—"}</div>
              </div>
              <span className="bgd-route-arrow">
                <MdArrowForward />
              </span>
              <div className="bgd-route-col">
                <div className="bgd-route-label">Куда</div>
                <div className="bgd-route-station">{trip?.destinationStation?.name || "—"}</div>
              </div>
            </div>

            <hr className="bgd-divider" />

            <div className="bgd-fields">
              <Field label="Поезд" value={trip?.train?.number || "—"} />
              <Field label="Дата отправления" value={trip?.departureDate || "—"} />
              <Field label="Номер билета" value={trip?.ticketNumber || "—"} />
            </div>
          </div>

          {/* Параметры груза */}
          <div className="bgd-card">
            <SectionHeader icon={<MdInventory2 />} title="Параметры груза" />
            <div className="bgd-fields">
              <Field label="Вес (всего)" value={totalWeight > 0 ? `${totalWeight} кг` : "—"} />
              <Field label="Количество мест" value={pieces.length} />
              <Field
                label="Объявленная ценность (₸)"
                value={declaredValueText}
                muted={!hasDeclaredValue}
              />
              <Field
                label="Описание"
                value={cargoDescription}
                wide
                muted={cargoDescription === "—"}
              />
            </div>
          </div>

          {/* Места багажа */}
          <div className="bgd-card">
            <SectionHeader
              icon={<MdLuggage />}
              title="Места багажа"
              action={<span className="bgd-section-count">{pieces.length}</span>}
            />
            {pieces.length === 0 ? (
              <div className="bgd-empty">
                <MdLuggage size={36} />
                <div style={{ marginTop: 8 }}>Места ещё не добавлены</div>
                <Link to={`/admin/baggages/add?tripID=${id}`} className="bgd-link">
                  <button className="bgd-btn" style={{ marginTop: 12 }}>
                    <MdAdd /> Добавить багаж
                  </button>
                </Link>
              </div>
            ) : (
              <table className="bgd-table">
                <thead>
                  <tr>
                    <th>Наклейка</th>
                    <th>Тип</th>
                    <th>Вес</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {pieces.map((p: any) => (
                    <tr key={p.id}>
                      <td className="bgd-tag">{p.tagCode}</td>
                      <td>{p.type}</td>
                      <td>{p.weight != null ? `${Number(p.weight)} кг` : "—"}</td>
                      <td>
                        <Pill variant={statusPill(p.status)} label={baggageStatusLabel(p.status)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* История отправки — единая лента по всем местам */}
          <div className="bgd-card">
            <SectionHeader icon={<MdHistory />} title="История отправки" />
            {history.length === 0 ? (
              <div className="bgd-field-value muted">Записей пока нет.</div>
            ) : (
              <div className="bgd-timeline">
                {history.map((h: any, i: number) => {
                  const isLast = i === history.length - 1;
                  const allPieces = pieces.length > 1 && h.tags.length === pieces.length;
                  return (
                    <div className="bgd-tl-item" key={h.key}>
                      <div className="bgd-tl-rail">
                        <span className={`bgd-tl-dot ${statusPill(h.status)}`} />
                        {!isLast && <span className="bgd-tl-line" />}
                      </div>
                      <div className={`bgd-tl-body${isLast ? "" : " has-gap"}`}>
                        <div className="bgd-tl-top">
                          <Pill variant={statusPill(h.status)} label={baggageStatusLabel(h.status)} />
                          <span className="bgd-tl-time">{formatDateTime(h.createdAt)}</span>
                        </div>

                        {(h.station?.name || h.train?.number || h.scanner?.name) && (
                          <div className="bgd-tl-meta">
                            {h.station?.name && (
                              <span>
                                <MdPlace /> {h.station.name}
                              </span>
                            )}
                            {h.train?.number && (
                              <span>
                                <MdTrain /> {h.train.number}
                              </span>
                            )}
                            {h.scanner?.name && (
                              <span>
                                <MdPerson /> {h.scanner.name}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="bgd-tl-pieces">
                          {h.tags.length === 1 ? (
                            <span className="bgd-tl-tag">{h.tags[0]}</span>
                          ) : (
                            <>
                              <span className="bgd-tl-count">
                                {allPieces
                                  ? `Все места (${h.tags.length})`
                                  : `${h.tags.length} ${piecesWord(h.tags.length)}`}
                              </span>
                              <span className="bgd-tl-taglist">{h.tags.join(", ")}</span>
                            </>
                          )}
                        </div>

                        {h.note && <div className="bgd-tl-note">{h.note}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Боковая колонка */}
        <div className="bgd-col">
          {/* Отправитель */}
          <div className="bgd-card">
            <SectionHeader icon={<MdPerson />} title="Отправитель" />
            <div className="bgd-person">
              <span
                className="bgd-avatar"
                style={{ background: colorFromString(String(customer?.id || customerName)) }}
                aria-label={customerName}
              >
                {initials(customerName)}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="bgd-person-name">{customerName}</div>
                <div className="bgd-person-sub">
                  {isLegal ? "Юридическое лицо" : "Физическое лицо"}
                </div>
              </div>
            </div>
            <div className="bgd-fields">
              {isLegal && trip?.companyName ? (
                <Field label="Компания" value={trip.companyName} wide />
              ) : null}
              {isLegal && trip?.companyBin ? (
                <Field label="БИН / ИИН" value={trip.companyBin} mono />
              ) : customerIin ? (
                <Field label="ИИН" value={customerIin} mono />
              ) : null}
            </div>
          </div>

          {/* Получатель */}
          <div className="bgd-card">
            <SectionHeader icon={<MdPersonOutline />} title="Получатель" />
            {hasSeparateRecipient ? (
              <div className="bgd-fields">
                <Field label="ФИО" value={recipientName} />
                <Field label="Телефон" value={trip?.recipientPhone || "—"} />
              </div>
            ) : (
              <div className="bgd-field-value muted">Совпадает с отправителем — {customerName}</div>
            )}
          </div>

          {/* К оплате */}
          <div className="bgd-card">
            <div className="bgd-price-label">К оплате</div>
            <div className="bgd-price">
              {Number(trip?.totalPrice || 0).toLocaleString("ru-RU")} {currency}
            </div>
            <div className="bgd-pay-row">
              Статус:
              <Pill
                variant={PAYMENT_PILL[paymentStatus] || "bgd-pill--gray"}
                label={PAYMENT_LABELS[paymentStatus] || paymentStatus}
              />
            </div>

            <hr className="bgd-divider" />

            {paid ? (
              <div className="bgd-paid">
                <MdCheckCircle size={32} />
                <div style={{ marginTop: 4 }}>Оплата по отправке принята</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div className="bgd-field-block">
                  <label className="bgd-label" htmlFor="bgd-method">
                    Способ оплаты
                  </label>
                  <select
                    id="bgd-method"
                    className="bgd-select"
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                  >
                    <option value="cash">Наличные</option>
                    <option value="card">Карта</option>
                    <option value="transfer">Перевод</option>
                  </select>
                </div>
                <button
                  className="bgd-btn bgd-btn--primary"
                  disabled={submitting}
                  onClick={handlePay}
                >
                  <MdPayments /> {submitting ? "Принимаем..." : "Принять оплату"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Панель действий */}
      <div className="bgd-actions">
        {canDeliver && (
          <button
            className="bgd-btn bgd-btn--primary"
            disabled={delivering}
            onClick={handleDeliver}
          >
            <MdCheckCircle /> {delivering ? "Выдаём..." : "Выдать"}
          </button>
        )}
        <button className="bgd-btn bgd-btn--accent" onClick={handleWaybill}>
          <MdDescription /> Скачать накладную
        </button>
        <button className="bgd-btn" onClick={handleLabels}>
          <MdLuggage /> Печать наклеек
        </button>
        <button className="bgd-btn" onClick={handleReceipt}>
          <MdReceiptLong /> Квитанция
        </button>
      </div>
    </div>
  );
};

export default withRouter(
  withRemoteDataAndSpinner(BaggageTripDetail, (router: any) => {
    const requests: any = {
      trip: () => baggageTripsService.get(router.params.id),
    };
    return useLoadAll(requests);
  }),
);
